// Price-list import parsing (Module 09 §4.3's import lane) — pure,
// unit-tested, NO floats anywhere near a price.
//
// THE LANE, honestly: §4.3 says "Excel import"; parsing .xlsx client-side
// needs a new dependency, and no new dependency lands without a DECISIONS.md
// entry — so part 2 ships CSV/TSV file + paste (what Excel itself exports and
// what «Ctrl+C» from a sheet produces: TSV), and the spec sentence is amended
// to state it. Excel-native import can ride module 02's server lanes later.
//
// Columns: `sku`, `uom`, `min_qty` (optional, defaults 0), `price` — matched
// by HEADER NAME, case-insensitive, any order, extra columns ignored. The
// delimiter is sniffed off the header line: a tab anywhere means TSV
// (spreadsheet paste), else comma.
//
// EXACTNESS: `price` is a decimal MAJOR amount ("1250.50") converted to
// integer minor units by string/BigInt arithmetic (`decimalToMinor`) — a
// float multiply here is how 1250.50 KZT becomes 125049. More fractional
// digits than the currency has is a REFUSAL, never a rounding: the file says
// something the wire cannot carry, and silently rounding a price is inventing
// one.

import type { TFunction } from "i18next";
import { enumLabel } from "../../../../lib/enum-label";
/** One parsed data row, 1-based `line` for the report grammar. */
export interface ParsedPriceRow {
  readonly line: number;
  readonly sku: string;
  readonly uom: string;
  /** Decimal text, ≤ 3 dp; defaults to "0" when the column is absent/empty. */
  readonly minQty: string;
  /** Decimal MAJOR price text, verbatim from the file. */
  readonly priceText: string;
}

export type PriceParseErrorKind = "columns" | "price" | "minQty";

export interface PriceParseError {
  readonly line: number;
  readonly kind: PriceParseErrorKind;
}

export interface PriceParseResult {
  readonly rows: readonly ParsedPriceRow[];
  readonly errors: readonly PriceParseError[];
  /** True when the header row does not name the required columns at all —
   * nothing below it was read. */
  readonly headerError: boolean;
}

const MIN_QTY = /^\d{1,11}(\.\d{1,3})?$/u;
const PRICE = /^\d{1,15}(\.\d{1,6})?$/u;

function splitLine(line: string, delimiter: string): string[] {
  // CSV quoting: good enough for the two shapes Excel emits — quoted fields
  // with embedded delimiters and doubled quotes. TSV needs none of it.
  if (delimiter === "\t" || !line.includes('"')) {
    return line.split(delimiter).map((cell) => cell.trim());
  }
  const out: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    // `charAt` rather than `line[i]`: with `noUncheckedIndexedAccess` the
    // index form is `string | undefined`, and appending that to a cell would
    // put the four characters "undefined" inside a SKU.
    const ch = line.charAt(i);
    if (quoted) {
      if (ch === '"') {
        if (line.charAt(i + 1) === '"') {
          cell += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === delimiter) {
      out.push(cell.trim());
      cell = "";
    } else {
      cell += ch;
    }
  }
  out.push(cell.trim());
  return out;
}

/** Parse CSV/TSV text (file or paste) into rows + per-line errors. */
export function parsePriceTable(text: string): PriceParseResult {
  const lines = text.split(/\r\n|\r|\n/u);
  let headerIndex = -1;
  for (let i = 0; i < lines.length; i += 1) {
    if ((lines[i] ?? "").trim().length > 0) {
      headerIndex = i;
      break;
    }
  }
  if (headerIndex === -1) return { rows: [], errors: [], headerError: true };

  const headerLine = lines[headerIndex] ?? "";
  const delimiter = headerLine.includes("\t") ? "\t" : ",";
  const header = splitLine(headerLine, delimiter).map((h) =>
    h.toLowerCase().replace(/[\s_-]+/gu, "_"),
  );
  const col = (name: string): number => header.indexOf(name);
  const skuAt = col("sku");
  const uomAt = col("uom");
  const minQtyAt = col("min_qty");
  const priceAt = col("price");
  if (skuAt === -1 || uomAt === -1 || priceAt === -1) {
    return { rows: [], errors: [], headerError: true };
  }

  const rows: ParsedPriceRow[] = [];
  const errors: PriceParseError[] = [];
  for (let i = headerIndex + 1; i < lines.length; i += 1) {
    const raw = lines[i] ?? "";
    if (raw.trim().length === 0) continue;
    const line = i + 1; // 1-based, as a person counts lines in their file
    const cells = splitLine(raw, delimiter);
    const sku = cells[skuAt] ?? "";
    const uom = cells[uomAt] ?? "";
    const priceText = cells[priceAt] ?? "";
    const minQtyRaw = minQtyAt === -1 ? "" : (cells[minQtyAt] ?? "");
    if (sku.length === 0 || uom.length === 0 || priceText.length === 0) {
      errors.push({ line, kind: "columns" });
      continue;
    }
    if (!PRICE.test(priceText)) {
      errors.push({ line, kind: "price" });
      continue;
    }
    const minQty = minQtyRaw.length === 0 ? "0" : minQtyRaw;
    if (!MIN_QTY.test(minQty)) {
      errors.push({ line, kind: "minQty" });
      continue;
    }
    rows.push({ line, sku, uom, minQty, priceText });
  }
  return { rows, errors, headerError: false };
}

/**
 * Decimal MAJOR text → integer minor units, exactly. `undefined` when the
 * text is not a plain non-negative decimal, carries MORE fractional digits
 * than the currency has (refused, never rounded), or overflows the JSON-safe
 * bound the contract enforces (§9.16).
 */
export function decimalToMinor(text: string, digits: number): number | undefined {
  const match = /^(\d{1,15})(?:\.(\d+))?$/u.exec(text.trim());
  if (match === null) return undefined;
  const whole = match[1] ?? "0";
  const frac = match[2] ?? "";
  if (frac.length > digits) return undefined;
  const fracPadded = frac.padEnd(digits, "0");
  const minor =
    BigInt(whole) * 10n ** BigInt(digits) + (fracPadded.length > 0 ? BigInt(fracPadded) : 0n);
  if (minor > BigInt(Number.MAX_SAFE_INTEGER)) return undefined;
  return Number(minor);
}

/** The §5 PUT row this screen assembles. */
export interface AssembledItem {
  readonly product_id: string;
  readonly uom_id: string;
  readonly min_qty: string;
  readonly unit_price_minor: number;
}

/** Client-side pre-check rejections, in the same report grammar the server's
 * codes use. PRODUCT/UOM reuse the server vocabulary because the fact is the
 * same fact; PRICE_INVALID is client-only (the server never sees the text). */
export type ClientRowCode = "PRODUCT_NOT_FOUND" | "UOM_NOT_FOUND" | "PRICE_INVALID";

export interface AssembleResult {
  readonly items: readonly AssembledItem[];
  readonly rejected: readonly { line: number; code: ClientRowCode }[];
}

/**
 * Resolve parsed rows against the caller's SKU→product and UoM-code→id maps
 * (both case-insensitive on the lookup side) and convert prices to minor
 * units for the currency's `digits`.
 */
export function assemblePutItems(
  rows: readonly ParsedPriceRow[],
  productBySku: ReadonlyMap<string, string>,
  uomByCode: ReadonlyMap<string, string>,
  digits: number,
): AssembleResult {
  const items: AssembledItem[] = [];
  const rejected: { line: number; code: ClientRowCode }[] = [];
  for (const row of rows) {
    const productId = productBySku.get(row.sku.toLowerCase());
    if (productId === undefined) {
      rejected.push({ line: row.line, code: "PRODUCT_NOT_FOUND" });
      continue;
    }
    const uomId = uomByCode.get(row.uom.toLowerCase());
    if (uomId === undefined) {
      rejected.push({ line: row.line, code: "UOM_NOT_FOUND" });
      continue;
    }
    const minor = decimalToMinor(row.priceText, digits);
    if (minor === undefined) {
      rejected.push({ line: row.line, code: "PRICE_INVALID" });
      continue;
    }
    items.push({
      product_id: productId,
      uom_id: uomId,
      min_qty: row.minQty,
      unit_price_minor: minor,
    });
  }
  return { items, rejected };
}

/** A rejected row's reason in the reader's words. A code this screen has no
 * name for reads as "Other", never as the code itself. */
export function rowCodeLabel(t: TFunction, code: string): string {
  return enumLabel(t, "sales.priceListDetail.rowCode", code);
}
